import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Star, MapPin, CheckCircle, Loader2, LogIn, Calendar, LayoutGrid } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BusinessCandidate } from '../types'
import { businessApi, scanApi } from '../api/client'
import { useScanStore } from '../stores/scanStore'
import { useAuthStore } from '../stores/authStore'
import { formatNumber } from '../lib/utils'
import { APP_NAME, BRAND, CONSULTATION_URL, FOOTER } from '../config'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import logoHeader from '../assets/landing/logo-header.svg'
import logoFooter from '../assets/landing/logo-footer.svg'

// ─── Social icons (footer) ────────────────────────────────────────────────────
function SocialIcon({ name }: { name: 'instagram' | 'facebook' | 'youtube' }) {
  const paths: Record<'instagram' | 'facebook' | 'youtube', React.ReactNode> = {
    instagram: (
      <path
        fill="currentColor"
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
      />
    ),
    facebook: (
      <path
        fill="currentColor"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    ),
    youtube: (
      <path
        fill="currentColor"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
      />
    ),
  }

  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden>
      {paths[name]}
    </svg>
  )
}

function CartleyLogo({ variant = 'header' }: { variant?: 'header' | 'footer' }) {
  const src = variant === 'footer' ? logoFooter : logoHeader
  const height = variant === 'footer' ? 'h-[61px]' : 'h-11 sm:h-14 md:h-[62px]'

  return <img src={src} alt={APP_NAME} className={`${height} w-auto`} />
}

// ─── Business result card ─────────────────────────────────────────────────────
function BusinessCard({
  candidate,
  selecting,
  disabled,
  onSelect,
  gradeLabel,
  reviewsLabel,
}: {
  candidate: BusinessCandidate
  selecting: boolean
  disabled: boolean
  onSelect: () => void
  gradeLabel: string
  reviewsLabel: string
}) {
  const location = [candidate.address, candidate.city].filter(Boolean).join(', ')

  return (
    <div className="bg-white rounded-[14px] min-h-[102px] p-3.5 flex items-center gap-3.5 hover:shadow-sm transition-shadow">
      <div className="w-14 h-14 rounded-xl bg-[#f3f4f6] overflow-hidden shrink-0">
        {candidate.photo_url ? (
          <img src={candidate.photo_url} alt={candidate.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xl">🏢</div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <h3 className="font-bold text-sm text-[#101828] truncate leading-5">{candidate.name}</h3>
          {candidate.is_verified && (
            <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" strokeWidth={2} />
          )}
        </div>

        {location && (
          <div className="group/addr relative flex items-center gap-1 text-xs text-[#6a7282] mb-1 leading-4">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{location}</span>
            {/* Full address revealed on hover — keeps every card the same height. */}
            <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-max max-w-60 rounded-lg bg-[#101828] px-2.5 py-1.5 text-[11px] font-medium leading-4 text-white shadow-lg group-hover/addr:block">
              {location}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#6a7282] leading-4">
          <span className="inline-flex items-center gap-1">
            <Star className="w-3 h-3 text-yellow-500 shrink-0" fill="#f59e0b" />
            {candidate.rating.toFixed(1)}
            {' '}
            ({formatNumber(candidate.review_count)} {reviewsLabel})
          </span>
          {(candidate.resolved_category || candidate.category) && (
            <span className="text-[#99a1af]">
              {candidate.resolved_category || candidate.category}
            </span>
          )}
        </div>
      </div>

      <div className="shrink-0 self-center">
        {selecting ? (
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND.red }} />
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation()
              onSelect()
            }}
            className="text-white text-xs font-medium h-8 px-3.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-60"
            style={{ backgroundColor: BRAND.red }}
            onMouseOver={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = BRAND.redHover }}
            onMouseOut={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = BRAND.red }}
          >
            {gradeLabel}
          </button>
        )}
      </div>
    </div>
  )
}

export function SearchPage() {
  const navigate = useI18nNavigate()
  const [searchParams] = useSearchParams()
  const [candidates, setCandidates] = useState<BusinessCandidate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [nextPageToken, setNextPageToken] = useState('')
  const [selecting, setSelecting] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Guards against duplicate concurrent "load next page" requests (e.g. the
  // sentinel firing repeatedly while a fetch is already in flight).
  const loadingMoreRef = useRef(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const { t, i18n } = useTranslation('search')
  const { t: tCommon } = useTranslation('common')
  const { t: tLanding } = useTranslation('landing')

  const initScan = useScanStore(s => s.initScan)
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)

  const query = searchParams.get('q') ?? ''
  const city = searchParams.get('city') ?? ''

  const shellHeader = 'max-w-[1070px] mx-auto w-full px-5 sm:px-6'
  const shellContent = 'max-w-[1320px] mx-auto w-full px-5 sm:px-6'
  const shellFooter = 'max-w-[1320px] mx-auto w-full px-5 sm:px-6 lg:px-8'
  const footerYear = new Date().getFullYear()

  useEffect(() => {
    if (!query) return
    setLoading(true)
    // Reset paging state whenever the query/city changes.
    setNextPageToken('')
    loadingMoreRef.current = false
    businessApi.search(query, city)
      .then(({ candidates, nextPageToken }) => {
        setCandidates(candidates)
        setNextPageToken(nextPageToken)
      })
      .catch(() => setError(t('error.searchFailed')))
      .finally(() => setLoading(false))
  }, [query, city, t])

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !nextPageToken) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    businessApi.search(query, city, nextPageToken)
      .then(({ candidates: more, nextPageToken: next }) => {
        // De-duplicate by external_id in case Google returns overlap between pages.
        setCandidates((prev) => {
          const seen = new Set(prev.map((c) => c.external_id))
          return [...prev, ...more.filter((c) => !seen.has(c.external_id))]
        })
        setNextPageToken(next)
      })
      .catch(() => { /* keep existing results; a failed page is non-fatal */ })
      .finally(() => {
        loadingMoreRef.current = false
        setLoadingMore(false)
      })
  }, [query, city, nextPageToken])

  // Infinite scroll: fetch the next page when the sentinel scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !nextPageToken) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { rootMargin: '400px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [nextPageToken, loadMore])

  const handleSelect = async (candidate: BusinessCandidate) => {
    setSelecting(candidate.external_id)
    try {
      const biz = await businessApi.selectCandidate({
        external_id: candidate.external_id,
        source: candidate.source,
        name: candidate.name,
        address: candidate.address,
        city: candidate.city,
        state: candidate.state,
        country: candidate.country,
        phone: candidate.phone,
        website: candidate.website,
        category: candidate.category,
        latitude: candidate.latitude,
        longitude: candidate.longitude,
        profile_url: candidate.profile_url,
      })

      const scan = await scanApi.create(biz.id, i18n.language)
      initScan(scan.scan_id, biz.id, biz.name, biz.latitude, biz.longitude)
      navigate(`/scan/${scan.scan_id}`)
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined
      setError(message ?? t('error.generic'))
      setSelecting(null)
    }
  }

  return (
    <div className="min-h-screen bg-[#f2f2f6] flex flex-col">
      {/* Header */}
      <header className="bg-white sticky top-0 z-50">
        <div className={`${shellHeader} min-h-[100px] py-4 flex items-center justify-between gap-4`}>
          <CartleyLogo />

          <nav className="flex items-center justify-end gap-1.5 sm:gap-x-4">
            <LanguageSwitcher variant="compact" />
            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={tCommon('nav.book')}
              className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] px-0 text-xs font-medium text-white transition-colors whitespace-nowrap md:h-[34px] md:w-auto md:px-5 md:text-sm"
              style={{ backgroundColor: BRAND.red }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
            >
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden md:inline">{tCommon('nav.book')}</span>
            </a>
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                aria-label={tCommon('nav.dashboard')}
                className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-[#01114f] bg-white px-0 text-xs font-medium text-[#01114f] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
              >
                <LayoutGrid className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{tCommon('nav.dashboard')}</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                aria-label={tCommon('nav.login')}
                className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-[#01114f] bg-white px-0 text-xs font-medium text-[#01114f] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
              >
                <LogIn className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{tCommon('nav.login')}</span>
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className={`${shellContent} flex-1 pt-8 pb-12 lg:pb-16`}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start gap-3 mb-8">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-1 text-[#4a5565] hover:text-[#101828] transition-colors shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-[#101828] leading-8">
                {t('title')}
              </h1>
              <p className="text-sm text-[#6a7282] mt-1 leading-5">
                {t('subtitle')}{' '}
                <strong className="font-bold text-[#101828]">
                  {t('subtitleQuery', { query })}
                </strong>
                {city ? t('subtitleCity', { city }) : ''}
              </p>
            </div>
          </div>
        </motion.div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#99a1af] animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {!loading && candidates.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[22px]">
              {candidates.map((c, i) => (
                <motion.div
                  key={c.external_id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  // Cap the stagger so appended pages don't animate with a huge delay.
                  transition={{ delay: Math.min(i, 8) * 0.06 }}
                  className="cursor-pointer"
                  onClick={() => !selecting && handleSelect(c)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !selecting) {
                      e.preventDefault()
                      handleSelect(c)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <BusinessCard
                    candidate={c}
                    selecting={selecting === c.external_id}
                    disabled={!!selecting}
                    onSelect={() => handleSelect(c)}
                    gradeLabel={t('gradeButton')}
                    reviewsLabel={t('reviews', { count: c.review_count })}
                  />
                </motion.div>
              ))}
            </div>

            {/* Infinite-scroll sentinel + loading indicator. */}
            {nextPageToken && (
              <div ref={sentinelRef} className="flex items-center justify-center py-8">
                {loadingMore && <Loader2 className="w-5 h-5 text-[#99a1af] animate-spin" />}
              </div>
            )}
          </>
        )}

        {!loading && candidates.length === 0 && !error && (
          <div className="text-center py-16">
            <p className="text-[#6a7282] mb-4">{t('empty.noResults')}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm font-medium hover:underline"
              style={{ color: BRAND.red }}
            >
              {t('empty.tryAgain')}
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: BRAND.navy }}>
        <div className={`${shellFooter} pt-8 pb-6`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
            <div>
              <CartleyLogo variant="footer" />
              <address className="not-italic text-base text-white mt-6 leading-6">
                <p>{tLanding('footer.address.line1')}</p>
                <p>{tLanding('footer.address.line2')}</p>
              </address>
            </div>

            <div>
              <p className="text-base font-medium mb-4" style={{ color: BRAND.red }}>
                {tLanding('footer.support')}
              </p>
              <ul className="space-y-3 text-base text-white">
                {!isAuthenticated && (
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="hover:opacity-80 transition-opacity"
                    >
                      {tCommon('nav.getStarted')}
                    </button>
                  </li>
                )}
                <li>
                  <a
                    href={CONSULTATION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    {tCommon('nav.book')}
                  </a>
                </li>
                <li>
                  <a
                    href={FOOTER.contactUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    {tLanding('footer.contact')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-base font-medium mb-4" style={{ color: BRAND.red }}>
                {tLanding('footer.follow')}
              </p>
              <div className="flex items-center gap-4">
                <a
                  href={FOOTER.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="text-white hover:opacity-80 transition-opacity"
                >
                  <SocialIcon name="instagram" />
                </a>
                <a
                  href={FOOTER.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="text-white hover:opacity-80 transition-opacity"
                >
                  <SocialIcon name="facebook" />
                </a>
                <a
                  href={FOOTER.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="text-white hover:opacity-80 transition-opacity"
                >
                  <SocialIcon name="youtube" />
                </a>
              </div>
            </div>
          </div>

          <hr className="border-t-2 border-white/30 my-8" />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-base text-white">
            <p>
              {tLanding('footer.copyright', { appName: APP_NAME, year: footerYear })}
            </p>
            <div className="flex flex-wrap gap-6 sm:justify-end">
              <a
                href={FOOTER.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                {tLanding('footer.terms')}
              </a>
              <a
                href={FOOTER.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                {tLanding('footer.privacy')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
